import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import '../styles/AddNormalInvoiceForm.css';
import API_BASE_URL from '../config/apiConfig.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import AddCustomerModal from '../components/modals/AddCustomerModal';
import AddTransporterModal from '../components/modals/AddTransporterModal';
import { usePermissionContext } from '../contexts/PermissionContext';
import Swal from 'sweetalert2';

const CreatableSelect = React.forwardRef(({ value, onChange, options, onAddOption, placeholder, creatable = true, validateOnEnter = false }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionMouseDown = (opt, e) => {
    e.preventDefault();
    onChange(opt);
    setSearchTerm(opt);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleBlur = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleAddClick = () => {
    onAddOption(searchTerm);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const filteredOptions = (Array.isArray(options) ? options : []).filter(opt =>
    opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const showAddOption = creatable && searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (isOpen) {
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          e.preventDefault();
          const selectedOpt = filteredOptions[activeIndex];
          onChange(selectedOpt);
          setSearchTerm(selectedOpt);
          setIsOpen(false);
          setActiveIndex(-1);
          return;
        } else if (showAddOption && activeIndex === filteredOptions.length) {
          e.preventDefault();
          handleAddClick();
          return;
        }
      } else {
        const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
        if (!hasValue && validateOnEnter) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      <input
        ref={ref}
        className="miracle-input"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder || "Select or Type..."}
      />
      {isOpen && (filteredOptions.length > 0 || showAddOption) && (
        <div className="custom-select-dropdown" ref={dropdownRef}>
          {filteredOptions.map((opt, index) => (
            <div
              key={opt}
              className="custom-select-option"
              style={{ backgroundColor: index === activeIndex ? '#e6f7ff' : 'transparent' }}
              onMouseDown={(e) => handleOptionMouseDown(opt, e)}
              tabIndex="-1"
            >
              {opt}
            </div>
          ))}
          {showAddOption && (
            <div
              className="custom-select-add-option"
              style={{ backgroundColor: filteredOptions.length === activeIndex ? '#e6f7ff' : 'transparent' }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddClick();
              }}
              tabIndex="-1"
            >
              Add "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const ProductCreatableSelect = React.forwardRef(({ value, onChange, products, category, onAddOption, placeholder , allowAdd = true }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (p) => {
    if (p === 'Other') {
      onChange('Other', null);
    } else {
      const calculatedRate = (parseFloat(p.product_sale_price || p.productSalePrice || 0)).toFixed(2);
      onChange(p.productName, { ...p, productSalePrice: calculatedRate, product_sale_price: calculatedRate });
    }
    setIsOpen(false);
    setActiveIndex(-1);
  };

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleBlur = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const filteredProducts = (products || []).filter(p => {
    const matchesCategory = !category || p.categoryName === category;
    const matchesSearch = (p.productName || '').toLowerCase().includes((searchTerm || '').toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const showAddOption = searchTerm && !filteredProducts.some(p => (p.productName || '').toLowerCase() === searchTerm.toLowerCase()) && !searchTerm.toLowerCase().includes('other');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      } else {
        const totalOptions = filteredProducts.length + (allowAdd ? 1 : 0);
        if (activeIndex >= 0 && activeIndex < filteredProducts.length) {
          e.preventDefault();
          handleOptionSelect(filteredProducts[activeIndex]);
          return;
        } else if (allowAdd && activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other');
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredProducts.length + (allowAdd ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      <input
        ref={ref}
        className="miracle-input"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder || "Select Product..."}
        style={{ width: '100%', border: 'none', background: 'transparent' }}
      />
      {isOpen && (filteredProducts.length > 0 || showAddOption || true) && (
        <div className="product-select-dropdown" ref={dropdownRef}>
          {filteredProducts.map((p, index) => {
            const hsn = p.productHsnCode || p.hsnCode || 'N/A';
            const stock = p.product_stock || p.productStock || p.stock || 0;
            const rate = (parseFloat(p.product_sale_price || p.productSalePrice || 0)).toFixed(2);
            const isSelected = index === activeIndex;

            return (
              <div
                key={p.id || p._id || index}
                className="product-select-option"
                style={{ backgroundColor: isSelected ? '#1e3a8a' : 'inherit' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleOptionSelect(p);
                }}
                tabIndex="-1"
              >
                <div className="product-option-name" style={{ color: isSelected ? '#fff' : '#1e293b' }}>{p.productName}</div>
                <div className="product-option-details" style={{ color: isSelected ? '#cbd5e1' : '#64748b' }}>
                  {hsn} <span className="product-divider" style={{ color: isSelected ? '#cbd5e1' : '#cbd5e1' }}>||</span>
                  <span className={stock > 0 ? "product-stock-green" : "product-stock-red"}>
                    AVL : {stock}
                  </span>
                  <span className="product-divider" style={{ color: isSelected ? '#cbd5e1' : '#cbd5e1' }}>||</span>
                  <span style={{ color: isSelected ? '#fff' : '#000', fontWeight: 'bold' }}>₹{parseFloat(rate).toFixed(2)}</span>
                </div>
              </div>
            );
          })}
          {allowAdd && (
          <div
            className="product-select-add-option"
            style={{ backgroundColor: activeIndex === filteredProducts.length ? '#f1f5f9' : '#fff', color: '#2563eb' }}
            onMouseDown={(e) => {
              e.preventDefault();
              handleOptionSelect('Other');
            }}
            tabIndex="-1"
          >
            Other (Add New Details)
          </div>
          )}
        </div>
      )}
    </div>
  );
});

const CustomerCreatableSelect = React.forwardRef(({ value, onChange, customers, onAddOption, placeholder, onSelect, onTab }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (c, fromKeyboard = false) => {
    const val = c === 'Other' ? 'Other' : (c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name);
    onChange(val, c);
    setSearchTerm(val);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(val, c), 0);
  };

  const filteredCustomers = (customers || []).filter(c => {
    const name = (c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name || '').toLowerCase();
    return name.includes((searchTerm || '').toLowerCase());
  });

  const showAddOption = searchTerm && !filteredCustomers.some(c => {
    const name = (c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name || '').toLowerCase();
    return name === searchTerm.toLowerCase();
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      } else {
        const totalOptions = filteredCustomers.length + 1;
        if (activeIndex >= 0 && activeIndex < filteredCustomers.length) {
          e.preventDefault();
          handleOptionSelect(filteredCustomers[activeIndex], true);
          return;
        } else if (activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other', true);
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredCustomers.length + 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <input
        ref={ref}
        className="miracle-input"
        style={{ width: '100%', boxSizing: 'border-box' }}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder || "Select Customer..."}
      />
      {isOpen && (filteredCustomers.length > 0 || showAddOption || true) && (
        <div className="po-dropdown" ref={dropdownRef} style={{ width: '400px', maxWidth: '90vw' }}>
          {filteredCustomers.map((c, index) => {
            const isSelected = index === activeIndex;
            const name = c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name;
            const gst = c.customerGst || c.customer_gst || 'N/A';
            const state = c.customerState || c.customer_state || c.customerCity || c.customer_city || 'N/A';
            
            // Format balance if exists, else default to 0.00 DR
            let balance = parseFloat(c.balance || c.openingBalance || c.opening_balance || 0).toFixed(2);
            let balType = c.balanceType || c.balance_type || 'DR';
            if (c.balance && !c.balanceType) {
              const val = parseFloat(c.balance);
              balance = Math.abs(val).toFixed(2);
              balType = val >= 0 ? 'DR' : 'CR';
            }
            
            return (
              <div
                key={c.id || c._id || index}
                className={`po-option ${isSelected ? 'active' : ''}`}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}
                onMouseDown={(e) => { e.preventDefault(); handleOptionSelect(c, false); }}
              >
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', marginBottom: '4px' }}>{name}</div>
                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>GST: {gst}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span>{state}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span style={{ color: '#3b82f6', fontWeight: '500' }}>Bal: {balance} {balType}</span>
                </div>
              </div>
            );
          })}
          <div
            className={`po-option ${activeIndex === filteredCustomers.length ? 'active' : ''}`}
            style={{ backgroundColor: activeIndex === filteredCustomers.length ? '#f1f5f9' : '#fff', color: '#2563eb', textAlign: 'center', cursor: 'pointer', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); handleOptionSelect('Other', false); }}
          >
            + Add New Party "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
});

const TransporterCreatableSelect = React.forwardRef(({ value, onChange, transporters, onAddOption, placeholder, onSelect, onTab, name }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (t, fromKeyboard = false) => {
    const val = t === 'Other' ? 'Other' : (t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name);
    onChange(val, t);
    setSearchTerm(val);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(val, t), 0);
  };

  const filteredTransporters = (transporters || []).filter(t => {
    const nameStr = (t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name || '').toLowerCase();
    return nameStr.includes((searchTerm || '').toLowerCase());
  });

  const showAddOption = searchTerm && !filteredTransporters.some(t => {
    const nameStr = (t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name || '').toLowerCase();
    return nameStr === searchTerm.toLowerCase();
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      } else {
        const totalOptions = filteredTransporters.length + 1;
        if (activeIndex >= 0 && activeIndex < filteredTransporters.length) {
          e.preventDefault();
          handleOptionSelect(filteredTransporters[activeIndex], true);
          return;
        } else if (activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other', true);
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredTransporters.length + 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <input
        name={name}
        ref={ref}
        className="miracle-input"
        style={{ width: '100%', boxSizing: 'border-box' }}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder || "Select Transport..."}
      />
      {isOpen && (filteredTransporters.length > 0 || showAddOption || true) && (
        <div className="po-dropdown" ref={dropdownRef} style={{ width: '400px', maxWidth: '90vw' }}>
          {filteredTransporters.map((t, index) => {
            const isSelected = index === activeIndex;
            const tName = t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name;
            const gst = t.transporterGst || t.transporter_gst || 'N/A';
            const state = t.transporterState || t.transporter_state || t.transporterCity || t.transporter_city || 'N/A';
            
            return (
              <div
                key={t.id || t._id || index}
                className={`po-option ${isSelected ? 'active' : ''}`}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}
                onMouseDown={(e) => { e.preventDefault(); handleOptionSelect(t, false); }}
              >
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', marginBottom: '4px' }}>{tName}</div>
                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>GST: {gst}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span>{state}</span>
                </div>
              </div>
            );
          })}
          <div
            className={`po-option ${activeIndex === filteredTransporters.length ? 'active' : ''}`}
            style={{ backgroundColor: activeIndex === filteredTransporters.length ? '#f1f5f9' : '#fff', color: '#2563eb', textAlign: 'center', cursor: 'pointer', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); handleOptionSelect('Other', false); }}
          >
            + Add New Transport "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
});

function AddNormalInvoiceForm({ isOpen, onClose, editingRow, isPage, isEdit }) {
  const { hasPermission } = usePermissionContext();
  const user = JSON.parse(sessionStorage.getItem('user'));

  // General Form State
  const [formData, setFormData] = useState({
    customer: '',
    saleType: '',
    invoiceName: 'AW',
    invoiceNo: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    transporter: '',
    lrNo: '',
    lrDate: '',
    vehicleNo: '',
    containerNo: '',
    sealNo: '',
    discountPercent: '0',
    insurance: '0',
    extraChargesName: 'Extra Charges',
    extraChargesAmount: '0',
    partyDeliveryAddress: '',
    paymentTerms: '100% PAYMENT AGAINST SCAN COPY OF DOCUMENTS. ',
    termsConditions: '',
    paymentType: 'Credit',
    invoiceShortName: '',
    isFromChallan: false,
    challanId: null,
    sourceChallanIds: []
  });

  const [customerBalance, setCustomerBalance] = useState('0.00 CR');
  const [showDeliveryAddress, setShowDeliveryAddress] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      if (formData.customer && formData.customer !== 'Other') {
        try {
          const token = sessionStorage.getItem('token');
          const response = await fetch(`${API_BASE_URL}/customer/${formData.customer}/balance`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setCustomerBalance(`${parseFloat(data.balance).toFixed(2)} ${data.balance >= 0 ? 'DR' : 'CR'}`);
          }
        } catch (err) {
          console.error("Error fetching balance:", err);
        }
      } else {
        setCustomerBalance('0.00 CR');
      }
    };
    fetchBalance();
  }, [formData.customer]);

  // Items State
  const [items, setItems] = useState([]);

  // Master Lists State
  const [customers, setCustomers] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [ownCompany, setOwnCompany] = useState(null);
  const [allCountries, setAllCountries] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [customerStateList, setCustomerStateList] = useState([]);
  const [allCustomerStates, setAllCustomerStates] = useState([]);
  const [customerCityList, setCustomerCityList] = useState([]);
  const [transporterStateList, setTransporterStateList] = useState([]);
  const [allTransporterStates, setAllTransporterStates] = useState([]);
  const [transporterCityList, setTransporterCityList] = useState([]);
  const [customerTypeList, setCustomerTypeList] = useState([]);
  const [invoiceNameList, setInvoiceNameList] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isTransporterModalOpen, setIsTransporterModalOpen] = useState(false);


  useEffect(() => {
    fetchMasterData();
    if (isEdit && editingRow) {
      // Standard Edit Mode
      setFormData({
        customer: editingRow.customerName || editingRow.customer_name || '',
        saleType: editingRow.saleTypeName || editingRow.sale_type_name || '',
        invoiceName: editingRow.invoiceTypeName || editingRow.invoice_type_name || editingRow.invoiceName || 'AW',
        invoiceNo: editingRow.invoiceNo || editingRow.invoice_no || '',
        invoiceDate: editingRow.invoiceDate || editingRow.invoice_date || '',
        transporter: editingRow.transporterName || editingRow.transporter_name || '',
        lrNo: editingRow.lrNo || editingRow.lr_no || '',
        lrDate: editingRow.lrDate || editingRow.lr_date || '',
        vehicleNo: editingRow.vehicleNo || editingRow.vehicle_no || '',
        containerNo: editingRow.containerNo || editingRow.container_no || '',
        sealNo: editingRow.sealNo || editingRow.seal_no || '',
        discountPercent: editingRow.discount || '0',
        insurance: editingRow.insurance || '0',
        extraChargesName: editingRow.extraChargesName || editingRow.extra_charges_name || 'Extra Charges',
        extraChargesAmount: editingRow.extraChargesAmount || editingRow.extrChargesAmount || editingRow.extr_charges_amount || '0',
        partyDeliveryAddress: editingRow.customerDeliveryAddress || editingRow.customer_delivery_address || '',
        paymentTerms: editingRow.paymentTerms || editingRow.payment_terms || '',
        termsConditions: editingRow.termsConditions || editingRow.terms_condition || editingRow.terms_conditions || '',
        invoiceShortName: editingRow.invoiceShortName || editingRow.invoice_short_name || '',
        isFromChallan: !!editingRow.isFromChallan,
        challanId: editingRow.challanId || null,
        sourceChallanIds: editingRow.sourceChallanIds || []
      });
      setShowDeliveryAddress(!!editingRow.customerDeliveryAddress);

      if (editingRow.items) {
        let itemsList = [];
        if (Array.isArray(editingRow.items)) {
          itemsList = editingRow.items;
        } else if (typeof editingRow.items === 'string') {
          try {
            itemsList = JSON.parse(editingRow.items);
          } catch (e) {
            console.error("Error parsing items string:", e);
            itemsList = [];
          }
        }
        if (Array.isArray(itemsList)) {
          setItems(itemsList.map(it => ({
            ...it,
            id: it._id || it.id || (Date.now() + Math.random()),
            newProduct: it.newProduct || {}
          })));
        }
      }
    } else if (editingRow && (editingRow.isFromChallan || !!editingRow.challanId)) {
      // Converting Sales Challan to Invoice: pre-fill data, backend skips stock decrement
      const _parseRawItems = (raw) => {
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string') { try { return _parseRawItems(JSON.parse(raw)); } catch(e) { return []; } }
        return [];
      };
      setFormData({
        customer: editingRow.customerName || editingRow.customer_name || '',
        saleType: editingRow.saleTypeName || editingRow.sale_type_name || editingRow.saleType || '',
        invoiceName: 'AW',
        invoiceNo: '',
        invoiceDate: new Date().toISOString().slice(0, 10),
        transporter: editingRow.transporterName || editingRow.transporter_name || '',
        lrNo: editingRow.lrNo || editingRow.lr_no || '',
        lrDate: editingRow.lrDate || editingRow.lr_date || '',
        vehicleNo: editingRow.vehicleNo || editingRow.vehicle_no || '',
        containerNo: editingRow.containerNo || editingRow.container_no || '',
        sealNo: editingRow.sealNo || editingRow.seal_no || '',
        discountPercent: editingRow.discount || '0',
        insurance: editingRow.insurance || '0',
        extraChargesName: editingRow.extraChargesName || editingRow.extra_charges_name || 'Extra Charges',
        extraChargesAmount: editingRow.extraChargesAmount || editingRow.extr_charges_amount || '0',
        partyDeliveryAddress: '',
        paymentTerms: editingRow.paymentTerms || editingRow.payment_terms || '100% PAYMENT AGAINST SCAN COPY OF DOCUMENTS. ',
        termsConditions: editingRow.termsConditions || editingRow.terms_condition || '',
        paymentType: editingRow.paymentType || editingRow.payment_type || 'Credit',
        invoiceShortName: '',
        isFromChallan: true,
        challanId: editingRow.challanId || editingRow.id,
        sourceChallanIds: editingRow.sourceChallanIds || [editingRow.challanId || editingRow.id]
      });
      const challanItems = _parseRawItems(editingRow.items);
      if (challanItems.length > 0) {
        setItems(challanItems.map(it => ({ 
          ...it, 
          id: it.id || it._id || (Date.now() + Math.random()), 
          newProduct: it.newProduct || {},
          sourceChallanId: it.sourceChallanId || (editingRow.challanId || editingRow.id)
        })));
      }
    } else {
      setFormData({
        customer: '',
        invoiceNo: '',
        invoiceDate: new Date().toISOString().slice(0, 10),
        transporter: '',
        lrNo: '',
        lrDate: '',
        vehicleNo: '',
        containerNo: '',
        sealNo: '',
        discountPercent: '0',
        insurance: '0',
        extraChargesName: 'Extra Charges',
        extraChargesAmount: '0',
        partyDeliveryAddress: '',
        paymentTerms: '100% PAYMENT AGAINST SCAN COPY OF DOCUMENTS. ',
        termsConditions: `1. Goods Once Supplied will not be taken back or exchanged.
2. We are not responsible for any shortage or damage in transist.
3. Interest at the rate of 18% shall be charged on all amounts unpaid within 15 days from the supply date.
4. Rs. 250 will be charged, when cheque would bounce.
5. Subject to MORBI Jurisdiction. E. & O.E`,
        paymentType: 'Credit',
        invoiceShortName: '',
        isFromChallan: false,
        challanId: null,
        sourceChallanIds: []
      });
      setCustomerBalance('0.00 CR');
      setItems([{
        id: Date.now(),
        category: '',
        product: '',
        hsnCode: '',
        grade: 'PREMIUM',
        modelNumber: '',
        color: '',
        sizeName: '',
        unit: 'PCS',
        quantity: '1',
        rate: '',
        total: '',
        newProduct: {}
      }]);
    }
  }, [isEdit, editingRow]);
  
  // Auto-fill Bill No based on Invoice Type
  useEffect(() => {
    const fetchNextNoForType = async () => {
      if (!isEdit && formData.invoiceName && formData.invoiceName !== 'Other') {
        try {
          const response = await fetch(`${API_BASE_URL}/sales-invoice/next-no?typeName=${encodeURIComponent(formData.invoiceName)}`, {
            headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
          });
          if (response.ok) {
            const data = await response.json();
            setFormData(prev => ({ ...prev, invoiceNo: data.nextNo }));
          }
        } catch (error) {
          console.error("Error fetching next invoice no:", error);
        }
      }
    };
    fetchNextNoForType();
  }, [formData.invoiceName, isEdit]);

  const firstInputRef = useRef(null);
  const customerGstRef = useRef(null);
  const transporterGstRef = useRef(null);
  const invoiceTypeRef = useRef(null);
  const lrNoRef = useRef(null);

  useEffect(() => {
    if (isOpen || isPage) {
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, isPage]);

  // Sync customer ID after customers list is loaded during edit
  useEffect(() => {
    const initialName = editingRow?.customerName || editingRow?.customer_name;
    if (isEdit && editingRow && customers.length && formData.customer === initialName) {
      // Find the customer that matches the editing row's customer ID or name
      const found = customers.find(c =>
        String(c.id || c._id) === String(editingRow.customer || editingRow.customerId) ||
        (c.customerName || c.customer_name) === initialName
      );
      if (found) {
        setFormData(prev => ({ ...prev, customer: found.id || found._id }));
      }
    }
  }, [isEdit, editingRow, customers, formData.customer]);

  const fetchMasterData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch all master data in parallel
      const [custRes, transRes, catRes, colRes, saleRes, invNameRes, prodRes, compRes, gradRes, countryRes, custTypeRes, invTitleRes] = await Promise.all([
        fetch(`${API_BASE_URL}/customer`, { headers }),
        fetch(`${API_BASE_URL}/transporter`, { headers }),
        fetch(`${API_BASE_URL}/category`, { headers }),
        fetch(`${API_BASE_URL}/color`, { headers }),
        fetch(`${API_BASE_URL}/sale-type?limit=0`, { headers }),
        fetch(`${API_BASE_URL}/invoice-type`, { headers }),
        fetch(`${API_BASE_URL}/product`, { headers }),
        user?.company_id ? fetch(`${API_BASE_URL}/company/${user.company_id}`, { headers }) : Promise.resolve({ ok: false }),
        fetch(`${API_BASE_URL}/grade`, { headers }),
        fetch(`${API_BASE_URL}/country`, { headers }),
        fetch(`${API_BASE_URL}/customer-type`, { headers }),
        fetch(`${API_BASE_URL}/invoice-name`, { headers })
      ]);

      if (custRes.ok) {
        const result = await custRes.json();
        setCustomers(result.customers || (Array.isArray(result) ? result : []));
      }
      if (transRes.ok) {
        const result = await transRes.json();
        setTransporters(result.transporters || (Array.isArray(result) ? result : []));
      }
      if (catRes.ok) {
        const result = await catRes.json();
        const cats = result.categories || (Array.isArray(result) ? result : []);
        setCategoryList(cats.map(c => c.categoryName).filter(Boolean));
      }
      if (colRes.ok) {
        const result = await colRes.json();
        const cols = result.colors || (Array.isArray(result) ? result : []);
        setColorList(cols.map(c => c.colorName).filter(Boolean));
      }
      if (saleRes.ok) {
        const result = await saleRes.json();
        setSaleTypes(result.saleTypes || (Array.isArray(result) ? result : []));
      }
      if (invNameRes.ok) {
        const result = await invNameRes.json();
        setInvoiceTypes(result.invoiceTypes || (Array.isArray(result) ? result : []));
      }
      if (prodRes.ok) {
        const result = await prodRes.json();
        setProducts(result.products || (Array.isArray(result) ? result : []));
      }
      if (gradRes.ok) {
        const result = await gradRes.json();
        const grads = result.grades || (Array.isArray(result) ? result : []);
        setGradeList(grads.map(g => g.gradeName).filter(Boolean));
      }
      if (compRes.ok) setOwnCompany(await compRes.json());
      if (countryRes.ok) {
        const countries = await countryRes.json();
        setAllCountries(countries);
        setCountryList(countries.map(c => c.country_name || c.countryName).filter(Boolean));
      }
      if (custTypeRes.ok) {
        const result = await custTypeRes.json();
        setCustomerTypeList(result.customerTypes || (Array.isArray(result) ? result : []));
      }
      if (invTitleRes.ok) {
        const result = await invTitleRes.json();
        const names = result.invoiceNames || (Array.isArray(result) ? result : []);
        setInvoiceNameList(names);
        if (!isEdit && names.length > 0) {
          const firstVal = names[0].invoiceShortName || names[0].invoice_short_name;
          if (firstVal) {
            setFormData(prev => ({ ...prev, invoiceName: firstVal }));
          }
        }
      }

    } catch (error) {
      console.error("Error fetching master data:", error);
      toast.error("Failed to load some form data");
    }
  };

  const handleAddCategory = async (newCat) => {
    if (!newCat) return;
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categoryName: newCat })
      });

      if (response.ok) {
        setCategoryList(prev => [...prev, newCat]);
        toast.success(`Category "${newCat}" Added Successfully!`);
      } else {
        const err = await response.json();
        toast.error(err.message || "Failed to add category to database");
      }
    } catch (error) {
      console.error("Add Category Error:", error);
      toast.error("Server error while adding category");
    }
  };

  const handleAddColor = async (newColor) => {
    if (!newColor) return;
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/color`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ colorName: newColor })
      });

      if (response.ok) {
        setColorList(prev => [...prev, newColor]);
        toast.success(`Color "${newColor}" Added Successfully!`);
      } else {
        const err = await response.json();
        toast.error(err.message || "Failed to add color to database");
      }
    } catch (error) {
      console.error("Add Color Error:", error);
      toast.error("Server error while adding color");
    }
  };

  const [newCustomer, setNewCustomer] = useState({
    gst: '', tradeName: '', name: '', refName: '', mobile: '',
    email: '', country: '', state: '', city: '', pincode: '', stateCode: '',
    pan: '', type: '', address: ''
  });
  const [newTransporter, setNewTransporter] = useState({
    gst: '', tradeName: '', name: '', refName: '', mobile: '',
    email: '', country: '', state: '', city: '', pincode: '', stateCode: '',
    pan: '', type: '', address: ''
  });

  useEffect(() => {
    const fetchStates = async (countryName, setStates, setAllStates) => {
      const country = allCountries.find(c => (c.country_name || c.countryName) === countryName);
      if (country) {
        const token = sessionStorage.getItem('token');
        try {
          const countryId = country.id || country.countryId;
          const res = await fetch(`${API_BASE_URL}/state?countryId=${countryId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const states = await res.json();
            if (setAllStates) setAllStates(states);
            setStates(states.map(s => s.state_name || s.stateName));
          }
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      } else {
        setStates([]);
      }
    };
    fetchStates(newCustomer.country, setCustomerStateList, setAllCustomerStates);
  }, [newCustomer.country, allCountries]);

  useEffect(() => {
    const fetchStates = async (countryName, setStates, setAllStates) => {
      const country = allCountries.find(c => (c.country_name || c.countryName) === countryName);
      if (country) {
        const token = sessionStorage.getItem('token');
        try {
          const countryId = country.id || country.countryId;
          const res = await fetch(`${API_BASE_URL}/state?countryId=${countryId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const states = await res.json();
            if (setAllStates) setAllStates(states);
            setStates(states.map(s => s.state_name || s.stateName));
          }
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      } else {
        setStates([]);
      }
    };
    fetchStates(newTransporter.country, setTransporterStateList, setAllTransporterStates);
  }, [newTransporter.country, allCountries]);

  useEffect(() => {
    const fetchCities = async (stateName, allStates, setCities) => {
      const state = allStates.find(s => (s.state_name || s.stateName) === stateName);
      if (state) {
        const token = sessionStorage.getItem('token');
        try {
          const stateId = state.id || state.stateId;
          const res = await fetch(`${API_BASE_URL}/city?stateId=${stateId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const cities = await res.json();
            setCities(cities.map(c => c.city_name || c.cityName));
          }
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      } else {
        setCities([]);
      }
    };
    fetchCities(newCustomer.state, allCustomerStates, setCustomerCityList);
  }, [newCustomer.state, allCustomerStates]);

  useEffect(() => {
    const fetchCities = async (stateName, allStates, setCities) => {
      const state = allStates.find(s => (s.state_name || s.stateName) === stateName);
      if (state) {
        const token = sessionStorage.getItem('token');
        try {
          const stateId = state.id || state.stateId;
          const res = await fetch(`${API_BASE_URL}/city?stateId=${stateId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const cities = await res.json();
            setCities(cities.map(c => c.city_name || c.cityName));
          }
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      } else {
        setCities([]);
      }
    };
    fetchCities(newTransporter.state, allTransporterStates, setTransporterCityList);
  }, [newTransporter.state, allTransporterStates]);

  // Auto-select Sale Type based on State Code (24 = Local 9+9, others = 18%)
  useEffect(() => {
    let stateCode = '';
    if (formData.customer === 'Other') {
      stateCode = String(newCustomer.stateCode || '').trim();
    } else if (formData.customer) {
      const selected = customers.find(c => (c._id || c.id) == formData.customer);
      if (selected) {
        stateCode = String(selected.customerStateCode || '').trim();
      }
    }

    if (stateCode) {
      if (isEdit && editingRow) {
        const initialCustomerName = editingRow.customerName || editingRow.customer_name;
        const initialCustomerId = editingRow.customer || editingRow.customerId;
        const isOriginalCustomer = 
          formData.customer === initialCustomerName ||
          formData.customer === initialCustomerId ||
          customers.some(c => (c.id || c._id) == formData.customer && (c.customerName || c.customer_name) === initialCustomerName);
          
        if (isOriginalCustomer && formData.saleType) {
          // Do not overwrite the saleType if we are in edit mode and the customer is the original one
          return;
        }
      }

      let bestSaleType = '';
      if (stateCode === '24') {
        const local = saleTypes.find(s =>
          (parseFloat(s.saleTypeTax1 || s.sale_type_tax1) === 9 && parseFloat(s.saleTypeTax2 || s.sale_type_tax2) === 9)
        );
        if (local) bestSaleType = local.saleTypeName;
      } else {
        const interstate = saleTypes.find(s =>
          (parseFloat(s.saleTypeTax1 || s.sale_type_tax1) === 18 || parseFloat(s.saleTypeTax2 || s.sale_type_tax2) === 18)
        );
        if (interstate) bestSaleType = interstate.saleTypeName;
      }

      if (bestSaleType && formData.saleType !== bestSaleType) {
        setFormData(prev => ({ ...prev, saleType: bestSaleType }));
      }
    }
  }, [formData.customer, newCustomer.stateCode, customers, saleTypes, isEdit, editingRow]);

  const updateNewCustomer = (field, value) => {
    setNewCustomer(prev => ({ ...prev, [field]: value }));
  };
  const updateNewTransporter = (field, value) => {
    setNewTransporter(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCountry = async (cName) => {
    if (!cName) return;
    try {
      const token = sessionStorage.getItem('token');
      const code = cName.substring(0, 3).toUpperCase();
      const response = await fetch(`${API_BASE_URL}/country`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ countryName: cName, countryCode: code })
      });

      if (response.ok) {
        const data = await response.json();
        const newCountry = data.country || { country_name: cName, id: Date.now() }; // Fallback if backend response structure differs
        setAllCountries(prev => [...prev, newCountry]);
        setCountryList(prev => [...prev, newCountry.country_name || newCountry.countryName]);
        toast.success(`Country "${cName}" added successfully`);
      } else {
        const err = await response.json();
        toast.error(err.message || "Failed to add country");
      }
    } catch (error) {
      console.error("Add Country Error:", error);
      toast.error("Server error while adding country");
    }
  };

  const handleAddState = async (newStateName, countryName, setStates, setAllStates) => {
    if (!newStateName) return;
    const country = allCountries.find(c => (c.country_name || c.countryName) === countryName);
    if (!country) {
      toast.warning("please select country then add new state");
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/state`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stateName: newStateName, countryId: country.id || country.countryId })
      });
      if (res.ok) {
        const data = await res.json();
        const newState = data.state || { state_name: newStateName, id: Date.now() };
        if (setAllStates) setAllStates(prev => [...prev, newState]);
        setStates(prev => [...prev, newState.state_name || newState.stateName]);
        toast.success(`State "${newStateName}" added successfully`);
      } else {
        const err = await res.json();
        toast.error(err.message || "Failed to add state");
      }
    } catch (error) {
      console.error("Add State Error:", error);
      toast.error("Server error while adding state");
    }
  };

  const handleAddCity = async (newCityName, stateName, allStates, setCities) => {
    if (!newCityName) return;
    const state = allStates.find(s => (s.state_name || s.stateName) === stateName);
    if (!state) {
      toast.warning("please select state or country then add new city", {
        style: { backgroundColor: '#ffcc00', color: 'black' }
      });
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/city`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cityName: newCityName, stateId: state.id || state.stateId })
      });

      if (res.ok) {
        const data = await res.json();
        const newCity = data.city || { city_name: newCityName, id: Date.now() };
        setCities(prev => [...prev, newCity.city_name || newCity.cityName]);
        toast.success(`City "${newCityName}" added successfully`);
      } else {
        const err = await res.json();
        toast.error(err.message || "Failed to add city");
      }
    } catch (error) {
      console.error("Add City Error:", error);
      toast.error("Server error while adding city");
    }
  };

  const handleChange = (e) => {
            let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }
            
        if (name.toLowerCase().includes('vehicle')) {
        setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
        return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, e) => {
            let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }
    setItems(prevItems => {
      const newItems = [...prevItems];
      let item = { ...newItems[index], [name]: value };

      // Reset product if category changes
      if (name === 'category') {
        item.product = '';
        item.productId = '';
        item.product_id = '';
        item.hsnCode = '';
        item.rate = '';
        item.total = '';
      }

      // Auto-calculate total
      if (name === 'quantity' || name === 'rate') {
        const qty = parseFloat(name === 'quantity' ? value : item.quantity) || 0;
        const rate = parseFloat(name === 'rate' ? value : item.rate) || 0;
        item.total = (qty * rate).toFixed(2);
      }

      newItems[index] = item;
      return newItems;
    });
  };

  const handleItemMultipleFieldsChange = (index, fields) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      let item = { ...newItems[index], ...fields };

      // Re-calculate total if quantity or rate changed
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      item.total = (qty * rate).toFixed(2);

      newItems[index] = item;
      return newItems;
    });
  };

  const updateItemNewProduct = (index, field, value) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      newItems[index].newProduct = {
        ...newItems[index].newProduct || {},
        [field]: value
      };
      return newItems;
    });
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      id: Date.now(),
      category: '',
      product: '',
      hsnCode: '',
      grade: 'PREMIUM',
      modelNumber: '',
      color: '',
      sizeName: '',
      unit: 'PCS',
      quantity: '1',
      rate: '',
      total: '',
      newProduct: {}
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(prev => prev.filter((_, i) => i !== index));
    } else {
      toast.warn("At least one product item is required.");
    }
  };

  // Keyboard navigation logic
  const handleFormKeyDown = (e) => {
    // Direct Jump to Insurance on TAB (Except when inside Add New subforms)
    if (e.key === 'Tab') {
      const isInsideSubform = e.target.closest('.add-customer-section');
      if (isInsideSubform) return; // Let local handlers or browser handle it

      const discountInput = document.querySelector('input[name="discountPercent"]');
      const insuranceInput = document.querySelector('input[name="insurance"]');
      const targetInput = discountInput || insuranceInput;
      if (targetInput && e.target !== targetInput) {
        e.preventDefault();
        targetInput.focus();
        targetInput.select();
        return;
      }
    }

    if (e.key === 'Enter' || e.key === 'ArrowLeft') {
      // Allow Enter to work naturally on textareas and buttons
      if (e.key === 'Enter' && (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.type === 'submit')) {
        return;
      }

      // Left Arrow only navigates if cursor is at the beginning
      if (e.key === 'ArrowLeft' && e.target.tagName === 'INPUT' && (e.target.selectionStart !== 0 || e.target.selectionEnd !== 0)) {
        return;
      }

      const form = e.currentTarget;
      const focusableElements = Array.from(form.querySelectorAll(
        'input:not([readonly]):not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button.miracle-submit-btn, button.miracle-cancel-btn'
      )).filter(el => {
        if (el.name === 'invoiceDate') return false; // Skip auto-filled date in keyboard flow
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && el.tabIndex !== -1;
      });

      const index = focusableElements.indexOf(e.target);
      if (index > -1) {
        if (e.key === 'Enter') {
          if (e.target.name === 'sealNo') {
            e.preventDefault();
            const firstCategory = form.querySelector('table.miracle-table tbody tr input, table.miracle-table tbody tr select');
            if (firstCategory) {
              firstCategory.focus();
              if (firstCategory.select) firstCategory.select();
              return;
            }
          }


          e.preventDefault();
          const nextElement = focusableElements[index + 1];
          if (nextElement) {
            nextElement.focus();
            if (nextElement.select) nextElement.select();
          }
        } else if (e.key === 'ArrowLeft') {
          const prevElement = focusableElements[index - 1];
          if (prevElement) {
            e.preventDefault();
            prevElement.focus();
            if (prevElement.select) prevElement.select();
          }
        }
      }
    }
  };

  const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;

    try {
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user'));

      // Mandatory Field Validation
      if (!formData.customer) {
        toast.error("Customer is mandatory");
        return;
      }
      if (!formData.invoiceNo) {
        toast.error("Invoice No is mandatory");
        return;
      }
      if (!formData.transporter) {
        toast.error("Transporter is mandatory");
        return;
      }

      // Filter out completely blank rows
      const activeItems = items.filter(row => row.category || row.product || (row.rate && row.rate !== ''));

      if (activeItems.length === 0) {
        toast.error("At least one product item is required.");
        return;
      }

      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i];
        if (!item.category) {
          toast.error(`Category is mandatory for Item ${i + 1}`);
          return;
        }
        if (!item.product) {
          toast.error(`Product is mandatory for Item ${i + 1}`);
          return;
        }

        if (!item.rate) {
          toast.error(`Rate is mandatory for Item ${i + 1}`);
          return;
        }

        // Permission Validations for New Category / Product
        if (item.category && !categoryList.some(c => c.toLowerCase() === item.category.toLowerCase())) {
          if (!hasPermission('Category', 'add')) {
            toast.error(`You do not have permission to add a new Category ("${item.category}"). Please select an existing category.`);
            return;
          }
        }
        if (item.product === 'Other' || (item.product && !products.some(p => (p.productName || '').toLowerCase() === item.product.toLowerCase()))) {
          if (!hasPermission('Product', 'add')) {
            const prodName = item.product === 'Other' ? item.newProduct?.name : item.product;
            toast.error(`You do not have permission to add a new Product ("${prodName}"). Please select an existing product.`);
            return;
          }
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Find details for selected entities
      let selectedCustomer = customers.find(c => (c._id || c.id) == formData.customer);
      if (!selectedCustomer) {
        selectedCustomer = customers.find(c => c.customerName === formData.customer);
      }
      let customerName = selectedCustomer ? selectedCustomer.customerName : formData.customer;

      // Handle 'Other' Customer
      if (formData.customer === 'Other') {
        if (!newCustomer.name) {
          toast.error("Customer name is mandatory for new customer");
          return;
        }
        try {
          const custPayload = {
            customerName: newCustomer.name,
            customerTradeName: newCustomer.tradeName,
            customerMobileNumber: newCustomer.mobile,
            customerEmail: newCustomer.email,
            customerGst: newCustomer.gst,
            customerPanNo: newCustomer.pan,
            customerCountry: newCustomer.country,
            customerState: newCustomer.state,
            customerCity: newCustomer.city,
            customerPinCode: newCustomer.pincode,
            customerAddress: newCustomer.address,
            customerState: newCustomer.state,
            customerReferenceName: newCustomer.refName,
            customerStateCode: newCustomer.stateCode,
            customerTypeId: newCustomer.type
          };

          const custRes = await fetch(`${API_BASE_URL}/customer`, {
            method: 'POST',
            headers,
            body: JSON.stringify(custPayload)
          });

          if (custRes.ok) {
            const custData = await custRes.json();
            selectedCustomer = custData.customer;
            customerName = selectedCustomer.customer_name || selectedCustomer.customerName;
            toast.success("New customer added successfully");
          } else {
            const custErr = await custRes.json();
            toast.error("Failed to add new customer: " + (custErr.message || "Unknown error"));
            return;
          }
        } catch (error) {
          console.error("Customer Creation Error:", error);
          toast.error("Server error while adding customer");
          return;
        }
      }

      let selectedTransporter = transporters.find(t => 
        (t.transporterTradeName || t.transporter_trade_name || t.transporterName) === formData.transporter ||
        t.transporterName === formData.transporter
      );
      let transporterName = selectedTransporter?.transporterName || formData.transporter;

      // Handle 'Other' Transporter
      if (formData.transporter === 'Other') {
        if (!newTransporter.name) {
          toast.error("Transporter name is mandatory for new transporter");
          return;
        }
        try {
          const transPayload = {
            transporterName: newTransporter.name,
            transporterTradeName: newTransporter.tradeName,
            transporterMobileNumber: newTransporter.mobile,
            transporterEmail: newTransporter.email,
            transporterGst: newTransporter.gst,
            transporterPanNo: newTransporter.pan,
            transporterCountry: newTransporter.country,
            transporterState: newTransporter.state,
            transporterCity: newTransporter.city,
            transporterPinCode: newTransporter.pincode,
            transporterAddress: newTransporter.address,
            transporterReferenceName: newTransporter.refName,
            transporterStateCode: newTransporter.stateCode
          };

          const transRes = await fetch(`${API_BASE_URL}/transporter`, {
            method: 'POST',
            headers,
            body: JSON.stringify(transPayload)
          });

          if (transRes.ok) {
            const transData = await transRes.json();
            selectedTransporter = transData.transporter;
            transporterName = selectedTransporter.transporter_name || selectedTransporter.transporterName;
            toast.success("New transporter added successfully");
          } else {
            const transErr = await transRes.json();
            toast.error("Failed to add new transporter: " + (transErr.message || "Unknown error"));
            return;
          }
        } catch (error) {
          console.error("Transporter Creation Error:", error);
          toast.error("Server error while adding transporter");
          return;
        }
      }

      const selectedSaleType = saleTypes.find(s => s.saleTypeName === formData.saleType);
      const selectedInvoiceType = invoiceTypes.find(i => i.invoiceTypeName === formData.invoiceName);

      // Calculate total amount (Subtotal - Discount + Insurance + Extra Charges = Taxable Amount; then Tax on Taxable Amount)
      const subTotal = activeItems.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);
      const discountPercent = parseFloat(formData.discountPercent) || 0;
      const discountAmount = (subTotal * discountPercent) / 100;

      const insurancePercentage = parseFloat(formData.insurance) || 0;
      const insuranceAmount = (subTotal * insurancePercentage) / 100;

      const extraChargesAmount = parseFloat(formData.extraChargesAmount) || 0;

      const taxableVal = subTotal - discountAmount + insuranceAmount + extraChargesAmount;

      const tax1 = parseFloat(selectedSaleType?.saleTypeTax1 || selectedSaleType?.sale_type_tax1) || 0;
      const tax2 = parseFloat(selectedSaleType?.saleTypeTax2 || selectedSaleType?.sale_type_tax2) || 0;
      const taxAmount = (taxableVal * (tax1 + tax2)) / 100;

      const totalAmount = taxableVal + taxAmount;


      const payload = {
        // Company
        invoiceCompanyId: ownCompany?.companyId,
        invoiceCompanyName: ownCompany?.companyName,
        invoiceCompanyMobileNumber: ownCompany?.companyMobileNumber,
        invoiceCompanyEmail: ownCompany?.companyEmail,
        invoiceCompanyGstNumber: ownCompany?.companyGstNumber,
        invoiceCompanyPanNumber: ownCompany?.companyPanNumber,
        invoiceCompanyAddress: ownCompany?.companyAddress,
        invoiceCompanyCountry: ownCompany?.companyCountry,
        invoiceCompanyState: ownCompany?.companyState,
        invoiceCompanyCity: ownCompany?.companyCity,
        invoiceCompanyPinCode: ownCompany?.companyPinCode,
        invoiceCompanyStateCode: ownCompany?.companyStateCode,
        invoiceCompanyBankName: ownCompany?.companyBankName,
        invoiceCompanyBankAccountName: ownCompany?.companyBankAccountName,
        invoiceCompanyBankAccountNumber: ownCompany?.companyBankAccountNumber,
        invoiceCompanyBankIfscCode: ownCompany?.companyBankIfscCode,
        invoiceCompanyBankAddress: ownCompany?.companyBankAddress,

        // Customer
        customer_id: selectedCustomer?.id || selectedCustomer?._id,
        customerName: customerName,
        customerTradeName: selectedCustomer?.customerTradeName,
        customerPinCode: selectedCustomer?.customerPinCode,
        customerAddress: selectedCustomer?.customerAddress,
        customerDeliveryAddress: formData.partyDeliveryAddress,
        customerGst: selectedCustomer?.customerGst,
        customerPanNo: selectedCustomer?.customerPanNo,
        customerPlaceOfSupply: selectedCustomer?.customerState || newCustomer.state || selectedCustomer?.customerPlaceOfSupply || editingRow?.customerPlaceOfSupply || editingRow?.customerState,
        customerState: selectedCustomer?.customerState || newCustomer.state || editingRow?.customerState,
        customerStateCode: selectedCustomer?.customerStateCode || newCustomer.stateCode || editingRow?.customerStateCode,

        // Transporter
        transporter_id: selectedTransporter?.id || selectedTransporter?._id,
        transporterName: transporterName,
        transporterTradeName: selectedTransporter?.transporterTradeName,
        transporterMobileNumber: selectedTransporter?.transporterMobileNumber,
        transporterEmail: selectedTransporter?.transporterEmail,
        transporterGstNumber: selectedTransporter?.transporterGstNumber,

        // Sale Type & Invoice Name
        sale_type_id: selectedSaleType?.id || selectedSaleType?._id,
        saleTypeName: formData.saleType,
        saleTypeTax1: selectedSaleType?.saleTypeTax1,
        saleTypeTax2: selectedSaleType?.saleTypeTax2,

        invoice_type_id: selectedInvoiceType?.id || selectedInvoiceType?._id,
        invoiceTypeName: formData.invoiceName,
        invoicePaymentTypeName: formData.paymentType,
        invoiceLock: 0,

        // Invoice Details
        invoiceNo: formData.invoiceNo,
        invoiceDate: formData.invoiceDate,
        vehicleNo: formData.vehicleNo,
        lrNo: formData.lrNo,
        lrDate: formData.lrDate,
        containerNo: formData.containerNo,
        sealNo: formData.sealNo,

        // Charges & Terms
        insurance: formData.insurance,
        discount: parseFloat(formData.discountPercent) || 0,
        discount_type: '%',
        extraChargesName: formData.extraChargesName,
        extrChargesAmount: formData.extraChargesAmount,
        paymentTerms: formData.paymentTerms,
        termsCondition: formData.termsConditions,

        // Products
        companyId: user.companyId,
        isFromChallan: formData.isFromChallan,
        challanId: formData.challanId,
        sourceChallanIds: formData.sourceChallanIds,
        items: activeItems.map(it => ({
          category: it.category,
          product: it.product === 'Other' ? (it.newProduct?.name || 'Other') : it.product,
          product_id: it.productId || it.product_id,
          hsnCode: it.product === 'Other' ? (it.newProduct?.hsn || it.hsnCode) : it.hsnCode,
          grade: it.grade,
          modelNumber: it.product === 'Other' ? (it.newProduct?.modelNumber || it.modelNumber) : it.modelNumber,
          color: it.color,
          sizeName: it.sizeName,
          sizeId: it.sizeId,
          unit: it.unit,
          quantity: parseFloat(it.quantity) || 0,
          rate: parseFloat(it.rate) || 0,
          total: parseFloat(it.total) || 0,
          sourceChallanId: it.sourceChallanId
        })),
        totalAmount: totalAmount,
      };

          try {
            const url = isEdit ? `${API_BASE_URL}/sales-invoice/${editingRow?._id || editingRow?.id}` : `${API_BASE_URL}/sales-invoice`;

            const method = isEdit ? 'PUT' : 'POST';

            const response = await fetch(url, {
              method,
              headers,
              body: JSON.stringify(payload)
            });

            if (response.ok) {
              toast.success(isEdit ? "Invoice Updated Successfully!" : "Invoice Created Successfully!");
              if (onClose) onClose();
            } else {
              const err = await response.json();
              toast.error(err.message || "Failed to save invoice");
            }
          } catch (error) {
            console.error("Submit Error:", error);
            toast.error("Server error while saving");
          }
    } catch (error) {
      console.error("Submit Error:", error);
      toast.error("Server error while saving");
    }
  };

  const selectedSaleTypeInfo = saleTypes.find(s => s.saleTypeName === formData.saleType);
  const tax1Rate = parseFloat(selectedSaleTypeInfo?.saleTypeTax1 || selectedSaleTypeInfo?.sale_type_tax1) || 0;
  const tax2Rate = parseFloat(selectedSaleTypeInfo?.saleTypeTax2 || selectedSaleTypeInfo?.sale_type_tax2) || 0;
  // isLocalSale: equal tax rates mean CGST/SGST (local). Unequal means IGST (interstate).
  const isLocalSale = tax1Rate > 0 && tax2Rate > 0 && tax1Rate === tax2Rate;

  const subTotalCalc = items.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);
  const totalQtyCalc = items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);
  const discountPercentCalc = parseFloat(formData.discountPercent) || 0;
  const discountAmountCalc = (subTotalCalc * discountPercentCalc) / 100;

  const insurancePercentageCalc = parseFloat(formData.insurance) || 0;
  const insuranceAmountCalc = (subTotalCalc * insurancePercentageCalc) / 100;
  const extraChargesAmountCalc = parseFloat(formData.extraChargesAmount) || 0;

  const taxableValCalc = subTotalCalc - discountAmountCalc + insuranceAmountCalc + extraChargesAmountCalc;

  const tax1AmountCalc = (taxableValCalc * tax1Rate) / 100;
  const tax2AmountCalc = (taxableValCalc * tax2Rate) / 100;
  const grandTotalCalc = taxableValCalc + tax1AmountCalc + tax2AmountCalc;


  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const billDay = formData.invoiceDate ? dayNames[new Date(formData.invoiceDate).getDay()] : "";

  if (!isOpen && !isPage) return null;

  const formContent = (
    <div className={isPage ? "page-card" : "modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
      {!isPage && (
        <div className="miracle-header">
          <h2 className="miracle-title">{isEdit ? 'Update Sales invoice' : 'Add Sales invoice'}</h2>
          <button type="button" className="invoice-close" onClick={onClose}>✕</button>
        </div>
      )}
      {isPage && <div className="page-card__title" style={{ textAlign: 'center', fontWeight: '500', fontSize: '24px', padding: '15px' }}>{isEdit ? 'Update Sales invoice' : 'Add Sales invoice'}</div>}

      <div className={isPage ? "page-card__body" : "miracle-body"}>
        <form onKeyDown={handleFormKeyDown} onSubmit={handleSubmit} style={isPage ? { padding: '10px 20px' } : {}}>

          {/* MIRACLE EXACT DESIGN TOP SECTION */}
          <div className="miracle-exact-top">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px 30px' }}>

              {/* Column 1: Payment & Party */}
              <div className="miracle-col-compact">
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Cash/Debit</label>
                  <CreatableSelect
                    ref={firstInputRef}
                    value={formData.paymentType}
                    options={['Debit', 'Credit', 'Cash Memo']}
                    onChange={(val) => handleChange({ target: { name: 'paymentType', value: val } })}
                    onAddOption={(val) => handleChange({ target: { name: 'paymentType', value: val } })}
                    placeholder="Payment Type"
                    creatable={false}
                  />
                </div>
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Party A/c.</label>
                  <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                    <div style={{ flex: 1 }}>
                      <CustomerCreatableSelect
                        value={customers.find(c => (c._id || c.id) == formData.customer)?.customerTradeName || (formData.customer === 'Other' ? 'Other' : formData.customer)}
                        customers={customers}
                        onChange={(val, selected) => {
                          if (val === 'Other') {
                            setIsCustomerModalOpen(true);
                            return;
                          }
                          if (selected) {
                            setFormData(prev => ({
                              ...prev,
                              customer: selected._id || selected.id,
                              partyDeliveryAddress: selected.customerAddress || ''
                            }));
                          } else {
                            setFormData(prev => ({ ...prev, customer: val }));
                          }
                        }}
                        onSelect={(val, selected) => {
                          // Allow moving to the next field on enter/select
                        }}
                        onAddOption={(val) => {
                          setIsCustomerModalOpen(true);
                        }}
                        placeholder="Select Party..."
                      />
                    </div>
                    <button type="button" className="btn-add-miracle" onClick={() => {
                      setIsCustomerModalOpen(true);
                    }}>Add New</button>
                  </div>
                </div>
                <div className="balance-text" style={{ marginLeft: '95px' }}>Bal. : {customerBalance}</div>
              </div>

              {/* Column 2: Invoice Type, Vehicle, Time */}
              <div className="miracle-col-compact">
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Time</label>
                  <input className="miracle-input" value={new Date().toLocaleTimeString('en-GB', { hour12: false })} readOnly />
                </div>
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Sale Type</label>
                  <CreatableSelect
                    value={formData.saleType}
                    options={saleTypes.map(s => s.saleTypeName)}
                    onChange={(val) => handleChange({ target: { name: 'saleType', value: val } })}
                    onAddOption={(val) => handleChange({ target: { name: 'saleType', value: val } })}
                    placeholder="Select Sale Type"
                    creatable={false}
                  />
                </div>
              </div>

              {/* Column 3: Bill Date, Bill No, Docs */}
              <div className="miracle-col-compact">
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Bill Date</label>
                  <div className="date-row-miracle">
                    <div style={{ flex: 1 }} className="miracle-date-picker miracle-date-picker-green">
                      <AirDatePicker
                        name="invoiceDate"
                        value={formData.invoiceDate}
                        onChange={(val) => handleChange({ target: { name: 'invoiceDate', value: val } })}
                        placeholder="dd/mm/yyyy"
                      />
                    </div>
                    <div className="day-label">{billDay}</div>
                  </div>
                </div>
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Bill No</label>
                  <input name="invoiceNo" value={formData.invoiceNo} onChange={handleChange} className="miracle-input miracle-input-green" />
                </div>
              </div>
            </div>

            <div className="miracle-divider"></div>

            {/* Transport & LR Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1.5fr) minmax(200px, 1fr) minmax(200px, 1fr)', gap: '40px' }}>
              <div className="miracle-row">
                <label>Transport</label>
                <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                  <div style={{ flex: 1 }}>
                    <TransporterCreatableSelect
                      value={formData.transporter}
                      transporters={transporters}
                      onChange={(val) => {
                        if (val === 'Other') {
                          setIsTransporterModalOpen(true);
                          return;
                        }
                        handleChange({ target: { name: 'transporter', value: val } });
                      }}
                      onAddOption={(val) => {
                        setIsTransporterModalOpen(true);
                      }}
                      placeholder="Select Transport"
                    />
                  </div>
                  <button type="button" className="btn-add-miracle" onClick={() => {
                    setIsTransporterModalOpen(true);
                  }}>Add New</button>
                </div>
              </div>
              <div className="miracle-row">
                <label>L.R. No.</label>
                <input
                  ref={lrNoRef}
                  id="lrNo-field"
                  name="lrNo"
                  value={formData.lrNo}
                  onChange={handleChange}
                  className="miracle-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('lrDate-field')?.focus(); } }}
                />
              </div>
              <div className="miracle-row">
                <label>L.R.Date</label>
                <div style={{ flex: 1 }} className="miracle-date-picker">
                  <AirDatePicker
                    id="lrDate-field"
                    value={formData.lrDate}
                    onChange={(val) => handleChange({ target: { name: 'lrDate', value: val } })}
                    placeholder="dd/mm/yyyy"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementsByName('vehicleNo')[0]?.focus(); } }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1.5fr) minmax(200px, 1fr) minmax(200px, 1fr)', gap: '40px', marginTop: '10px' }}>
              <div className="miracle-row">
                <label>Vehical No</label>
                <input name="vehicleNo" value={formData.vehicleNo} onChange={handleChange} className="miracle-input" />
              </div>
              <div className="miracle-row">
                <label>Container No</label>
                <input name="containerNo" value={formData.containerNo} onChange={handleChange} className="miracle-input" />
              </div>
              <div className="miracle-row">
                <label>Seal No</label>
                <input name="sealNo" value={formData.sealNo} onChange={handleChange} className="miracle-input" />
              </div>
            </div>
          </div>


          {/* Removed Inlined Forms */}

          {/* MIRACLE TABLE SECTION */}
          <div className="miracle-table-wrapper">
            <table className="miracle-table">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Category *</th>
                  <th style={{ width: '15%' }}>Product Name *</th>
                  <th style={{ width: '10%' }}>HSN Code</th>
                  <th style={{ width: '10%' }}>Grade</th>
                  <th style={{ width: '10%' }}>Color</th>
                  <th style={{ width: '8%' }}>Unit</th>
                  <th style={{ width: '8%', textAlign: 'right' }}>Qty</th>
                  <th style={{ width: '10%', textAlign: 'right' }}>Rate *</th>
                  <th style={{ width: '10%', textAlign: 'right' }}>Amount</th>
                  <th style={{ width: '4%', textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <CreatableSelect
                          value={item.category}
                          options={categoryList} creatable={hasPermission('Category', 'add')}
                          onChange={(val) => handleItemChange(index, { target: { name: 'category', value: val } })}
                          onAddOption={(newCat) => {
                            handleAddCategory(newCat);
                            handleItemChange(index, { target: { name: 'category', value: newCat } });
                          }}
                          placeholder="Type Category..."
                          validateOnEnter={true}
                        />
                      </td>
                      <td>
                        <ProductCreatableSelect
                          value={item.product}
                          products={products}
                          category={item.category}
                          allowAdd={hasPermission('Product', 'add')}
                          onChange={(val, selected) => {
                            if (val === 'Other') {
                              handleItemChange(index, { target: { name: 'product', value: 'Other' } });
                              // Sequential Navigation: Focus Name field in subform
                              setTimeout(() => {
                                const el = document.getElementById(`new-product-name-${index}`);
                                if (el) el.focus();
                              }, 100);
                              return;
                            }
                            if (selected) {
                              handleItemMultipleFieldsChange(index, {
                                product: val,
                                productId: selected.id || selected._id,
                                category: selected.Category?.categoryName || selected.Category?.category_name || selected.category_name || selected.categoryName || item.category,
                                product_id: selected.id || selected._id,
                                hsnCode: selected.productHsnCode || selected.hsnCode || '',
                                rate: selected.product_sale_price || selected.productSalePrice || selected.salesRate || '',
                                sizeName: selected.sizeName || '',
                                sizeId: selected.sizeId || '',
                                unit: selected.unit || 'PCS'
                              });
                            } else {
                              handleItemChange(index, { target: { name: 'product', value: val } });
                            }
                          }}
                          onAddOption={() => handleItemChange(index, { target: { name: 'product', value: 'Other' } })}
                        />
                      </td>
                      <td><input id={`row-hsnCode-${index}`} name="hsnCode" value={item.hsnCode} readOnly
                        style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`row-grade-${index}`)?.focus(); } }}
                      /></td>
                      <td>
                        <select id={`row-grade-${index}`} name="grade" value={item.grade} onChange={(e) => handleItemChange(index, e)} style={{ border: 'none', background: 'transparent', width: '100%', outline: 'none' }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.querySelector(`#row-color-container-${index} input`)?.focus(); } }}
                        >
                          <option value="">Grade</option>
                          {gradeList.map(g => (<option key={g} value={g}>{g}</option>))}
                        </select>
                      </td>
                      <td id={`row-color-container-${index}`}>
                        <CreatableSelect
                          value={item.color}
                          options={colorList}
                          onChange={(val) => {
                            handleItemChange(index, { target: { name: 'color', value: val } });
                            setTimeout(() => document.getElementById(`row-quantity-${index}`)?.focus(), 100);
                          }}
                          onAddOption={(newCol) => { handleAddColor(newCol); handleItemChange(index, { target: { name: 'color', value: newCol } }); setTimeout(() => document.getElementById(`row-quantity-${index}`)?.focus(), 100); }}
                          placeholder="Color"
                        />
                      </td>
                      <td><input name="unit" value={item.unit} onChange={(e) => handleItemChange(index, e)} /></td>
                      <td><input id={`row-quantity-${index}`} type="number" name="quantity" value={item.quantity} onChange={(e) => handleItemChange(index, e)} style={{ textAlign: 'right' }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`row-rate-${index}`)?.focus(); } }}
                      /></td>
                      <td><input id={`row-rate-${index}`} type="number" name="rate" value={item.rate} onChange={(e) => handleItemChange(index, e)} style={{ textAlign: 'right' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (index === items.length - 1) {
                              addItem();
                              setTimeout(() => {
                                const newRows = document.querySelectorAll('table.miracle-table tbody tr');
                                const firstInput = newRows[newRows.length - 1]?.querySelector('input, select');
                                firstInput?.focus();
                              }, 100);
                            } else {
                              const nextRows = document.querySelectorAll('table.miracle-table tbody tr');
                              const nextInput = nextRows[index + 1]?.querySelector('input, select');
                              nextInput?.focus();
                            }
                          }
                        }}
                      /></td>
                      <td><input name="total" value={item.total} readOnly style={{ textAlign: 'right' }} /></td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" onClick={() => removeItem(index)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', outline: 'none' }}>✕</button>
                      </td>
                    </tr>
                    {/* New Product Subform */}
                    {item.product === 'Other' && (
                      <tr>
                        <td colSpan={10} style={{ padding: '10px', backgroundColor: '#f9fafb' }}>
                          <div className="add-customer-section" style={{ margin: 0 }}>
                            <div className="add-customer-title">Add New Product Details</div>
                            <div className="add-customer-grid-row-6">
                              <div className="invoice-field">
                                <label className="invoice-label">Name</label>
                                <input
                                  id={`new-product-name-${index}`}
                                  className="invoice-input"
                                  value={item.newProduct?.name || ''}
                                  onChange={e => updateItemNewProduct(index, 'name', e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`new-product-hsn-${index}`)?.focus(); } }}
                                />
                              </div>
                              <div className="invoice-field">
                                <label className="invoice-label">HSN Code</label>
                                <input
                                  id={`new-product-hsn-${index}`}
                                  className="invoice-input"
                                  value={item.newProduct?.hsn || ''}
                                  onChange={e => updateItemNewProduct(index, 'hsn', e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`new-product-model-${index}`)?.focus(); } }}
                                />
                              </div>
                              <div className="invoice-field">
                                <label className="invoice-label">Model Number</label>
                                <input
                                  id={`new-product-model-${index}`}
                                  className="invoice-input"
                                  value={item.newProduct?.modelNumber || ''}
                                  onChange={e => updateItemNewProduct(index, 'modelNumber', e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`new-product-glaze-${index}`)?.focus(); } }}
                                />
                              </div>
                              <div className="invoice-field">
                                <label className="invoice-label">Finish Glaze</label>
                                <input
                                  id={`new-product-glaze-${index}`}
                                  className="invoice-input"
                                  value={item.newProduct?.finishGlaze || ''}
                                  onChange={e => updateItemNewProduct(index, 'finishGlaze', e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`new-product-price-${index}`)?.focus(); } }}
                                />
                              </div>
                              <div className="invoice-field">
                                <label className="invoice-label">Sale Price</label>
                                <input
                                  id={`new-product-price-${index}`}
                                  className="invoice-input"
                                  value={item.newProduct?.salePrice || ''}
                                  onChange={e => updateItemNewProduct(index, 'salePrice', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      
                                      const pName = item.newProduct?.name || 'Other';
                                      const pHsn = item.newProduct?.hsn || '';
                                      const pPrice = item.newProduct?.salePrice || '';
                                      const pModel = item.newProduct?.modelNumber || '';
                                      const pGlaze = item.newProduct?.finishGlaze || '';
                                      
                                      handleItemMultipleFieldsChange(index, {
                                        product: pName,
                                        hsnCode: pHsn,
                                        rate: pPrice,
                                        modelNumber: pModel,
                                        finishGlaze: pGlaze
                                      });
                                      
                                      setTimeout(() => {
                                        const el = document.getElementById(`row-grade-${index}`);
                                        if (el) {
                                          el.focus();
                                        }
                                      }, 100);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot className="miracle-tfoot" style={{ borderTop: '2px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                <tr>
                  <td colSpan="6" style={{ textAlign: 'left', padding: '8px 10px', fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Total Entry: {items.length}</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{totalQtyCalc.toFixed(3)}</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Sub Total</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{subTotalCalc.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ margin: '10px 0' }}>
            <button type="button" onClick={addItem} style={{ background: '#f2f4f7', border: '1px solid #c2c2c2', padding: '5px 15px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>+ Add Row</button>
          </div>

          {/* MIRACLE BOTTOM SECTION */}
          <div className="miracle-bottom-section">
            <div className="miracle-notes">
              <div>
                <label>Note / Payment Terms</label>
                <textarea className="miracle-textarea" name="paymentTerms" value={formData.paymentTerms} onChange={handleChange} />
              </div>
              <div>
                <label>Narration / Terms & Conditions</label>
                <textarea className="miracle-textarea" name="termsConditions" value={formData.termsConditions} onChange={handleChange} />
              </div>
              <div style={{ marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                  <input type="checkbox" checked={showDeliveryAddress} onChange={(e) => setShowDeliveryAddress(e.target.checked)} />
                  Change Delivery Address
                </label>
                {showDeliveryAddress && (
                  <input className="miracle-input" style={{ width: '100%', marginTop: '5px', height: '30px' }} name="partyDeliveryAddress" value={formData.partyDeliveryAddress} onChange={handleChange} placeholder="Enter Delivery Address" />
                )}
              </div>
            </div>
            <div className="miracle-summary">
              <div className="summary-row">
                <span>Discount (%)</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input className="miracle-input text-right" name="discountPercent" value={formData.discountPercent} onChange={handleChange} style={{ width: '60px' }} />
                  <span style={{ width: '20px', marginLeft: '5px' }}>%</span>
                </div>
                <span style={{ color: '#ef4444' }}>-{discountAmountCalc.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Insurance (%)</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input className="miracle-input text-right" name="insurance" value={formData.insurance} onChange={handleChange} style={{ width: '60px' }} />
                  <span style={{ width: '20px', marginLeft: '5px' }}>%</span>
                </div>
                <span>{insuranceAmountCalc.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Extra Charges</span>
                <input className="miracle-input text-right" name="extraChargesAmount" value={formData.extraChargesAmount} onChange={handleChange} style={{ width: '50px' }} />
              </div>
              <div className="summary-row bold">
                <span>Taxable Amount</span>
                <span>{taxableValCalc.toFixed(2)}</span>
              </div>
              {isLocalSale ? (
                <>
                  <div className="summary-row">
                    <span>CGST ({tax1Rate}%)</span>
                    <span>{tax1AmountCalc.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>SGST ({tax2Rate}%)</span>
                    <span>{tax2AmountCalc.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="summary-row">
                  <span>IGST ({(tax1Rate + tax2Rate).toFixed(2)}%)</span>
                  <span>{(tax1AmountCalc + tax2AmountCalc).toFixed(2)}</span>
                </div>
              )}
              <div className="summary-row grand-total">
                <span>Bill Amount</span>
                <span>{grandTotalCalc.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="miracle-actions">
            <button type="submit" className="miracle-submit-btn">OK / Save Invoice</button>
            <button type="button" className="miracle-cancel-btn" onClick={onClose}>Cancel</button>
          </div>

        </form>

        <AddCustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSuccess={(newCust) => {
            console.log("Customer added successfully, received data:", newCust);
            // Ensure we have a valid customer object with trade name
            const finalCust = {
              ...newCust,
              _id: newCust._id || newCust.id,
              id: newCust.id || newCust._id,
              customerTradeName: newCust.customerTradeName || newCust.customerName
            };
            
            if (!finalCust.id) {
              console.error("New customer object missing ID:", finalCust);
              toast.error("Error: New customer could not be selected automatically.");
              return;
            }

            setCustomers(prev => {
              const exists = prev.find(c => (c._id || c.id) === finalCust.id);
              if (exists) return prev;
              return [...prev, finalCust].sort((a, b) => 
                (a.customerTradeName || '').localeCompare(b.customerTradeName || '')
              );
            });

            setFormData(prev => ({
              ...prev,
              customer: finalCust.id,
              partyDeliveryAddress: finalCust.customerAddress || ''
            }));
            
            setIsCustomerModalOpen(false);
            toast.success(`Customer "${finalCust.customerTradeName}" added and selected.`);
            // Move focus to Invoice Name field
            setTimeout(() => invoiceTypeRef.current?.focus(), 150);
          }}
        />

        <AddTransporterModal
          isOpen={isTransporterModalOpen}
          onClose={() => setIsTransporterModalOpen(false)}
          onTransporterAdded={(newTrans) => {
            setTransporters(prev => [...prev, newTrans]);
            const selectedVal = newTrans.transporterTradeName || newTrans.transporter_trade_name || newTrans.transporterName;
            setFormData(prev => ({ ...prev, transporter: selectedVal }));
            setIsTransporterModalOpen(false);
            // Move focus to LR No field
            setTimeout(() => lrNoRef.current?.focus(), 150);
          }}
          onSuccess={(newTrans) => {
            setTransporters(prev => [...prev, newTrans]);
            const selectedVal = newTrans.transporterTradeName || newTrans.transporter_trade_name || newTrans.transporterName;
            setFormData(prev => ({ ...prev, transporter: selectedVal }));
            setIsTransporterModalOpen(false);
            // Move focus to LR No field
            setTimeout(() => lrNoRef.current?.focus(), 150);
          }}
        />

      </div>
    </div>
  );

  if (isPage) return formContent;

  return (
    <div className="modal-overlay" onClick={onClose}>
      {formContent}
    </div>
  );
}

export default AddNormalInvoiceForm;
